import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

const HUES = [212, 262, 168, 24, 336, 192, 140, 288, 8, 48];

function hue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length] ?? 212;
}

/** A soft, colour-coded tile with the item's initials (products have no images in search results). */
export function ItemTile({ name, size = 44 }: { name: string; size?: number }) {
  const h = hue(name);
  const words = name
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .trim()
    .split(/\s+/);
  const text = ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '•';
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: `${String(Math.round(size * 0.3))}px`,
        display: 'grid',
        placeItems: 'center',
        fontWeight: 700,
        fontSize: size * 0.34,
        letterSpacing: '-0.02em',
        color: `hsl(${String(h)} 70% 38%)`,
        background: `linear-gradient(135deg, hsl(${String(h)} 90% 94%), hsl(${String(h)} 80% 88%))`,
        border: `1px solid hsl(${String(h)} 60% 84%)`,
        '.dark &': {
          color: `hsl(${String(h)} 85% 78%)`,
          background: `linear-gradient(135deg, hsl(${String(h)} 45% 22%), hsl(${String(h)} 40% 16%))`,
          borderColor: `hsl(${String(h)} 35% 28%)`,
        },
      }}
    >
      {text}
    </Box>
  );
}

/** Keyboard hint, e.g. <Kbd>F9</Kbd>. */
export function Kbd({ children, inverse }: { children: ReactNode; inverse?: boolean }) {
  return (
    <Box
      component="kbd"
      sx={{
        fontFamily: 'inherit',
        fontSize: '0.6875rem !important',
        fontWeight: 650,
        lineHeight: 1,
        px: 0.75,
        py: 0.5,
        borderRadius: '6px',
        border: 1,
        borderColor: inverse ? 'rgba(255,255,255,0.35)' : 'divider',
        color: inverse ? 'rgba(255,255,255,0.9)' : 'text.secondary',
        bgcolor: inverse ? 'rgba(255,255,255,0.12)' : 'background.default',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}

/** Label / value row of a totals block. */
export function SumRow({
  label,
  value,
  muted,
  action,
}: {
  label: ReactNode;
  value: ReactNode;
  muted?: boolean;
  action?: ReactNode;
}) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        <Typography color={muted ? 'text.secondary' : 'text.primary'}>{label}</Typography>
        {action}
      </Stack>
      <Typography
        className="tabular"
        sx={{ fontWeight: 550 }}
        color={muted ? 'text.secondary' : 'text.primary'}
      >
        {value}
      </Typography>
    </Stack>
  );
}
